import { useCallback, useEffect, useState } from "react";
import { api, downloadWithPicker, formatBytes, offlineDbUrl, timeAgo, type OfflineDbInfo } from "../api.ts";

export function OfflineDbPanel({
  project,
  mediaCount,
  onMediaChanged,
}: {
  project: string;
  mediaCount: number;
  onMediaChanged?: () => void;
}) {
  const [info, setInfo] = useState<OfflineDbInfo | null>(null);
  const [rebuilding, setRebuilding] = useState(false);
  const [refingerprinting, setRefingerprinting] = useState(false);
  const [progress, setProgress] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [refingerprintEnabled, setRefingerprintEnabled] = useState(false);

  const loadInfo = useCallback(async () => {
    try {
      setInfo(await api.getOfflineDbInfo(project));
    } catch (e) {
      setError((e as Error).message);
    }
  }, [project]);

  useEffect(() => {
    loadInfo();
    api.getConfig().then((config) => setRefingerprintEnabled(config.refingerprintEnabled)).catch(() => {});
  }, [loadInfo]);

  async function onRebuild() {
    setRebuilding(true);
    setError(null);
    try {
      setInfo(await api.rebuildOfflineDb(project));
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setRebuilding(false);
    }
  }

  async function onRefingerprint() {
    setRefingerprinting(true);
    setError(null);
    setProgress(null);
    try {
      await api.startRefingerprint(project);
      // Poll until the background job finishes.
      for (;;) {
        await new Promise((r) => setTimeout(r, 1500));
        const job = await api.refingerprintStatus(project);
        setProgress(`${job.done}/${job.total}`);
        if (!job.running) {
          if (job.error) throw new Error(job.error);
          break;
        }
      }
      onMediaChanged?.();
      // Hashes changed: rebuild the .acrf so it reflects the new engine.
      setInfo(await api.rebuildOfflineDb(project));
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setRefingerprinting(false);
      setProgress(null);
    }
  }

  const stale = info !== null && info.songCount !== mediaCount;

  return (
    <div className="rounded-xl border border-slate-800 bg-slate-900/60 p-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="text-sm font-medium text-slate-200">Offline detection DB</div>
          <div className="mt-1 text-xs text-slate-400">
            {info ? (
              <>
                <span className="font-medium text-slate-300">v{info.version}</span> ·{" "}
                {info.songCount.toLocaleString()} sounds · {info.entryCount.toLocaleString()} hashes ·{" "}
                {formatBytes(info.sizeBytes)} · built {timeAgo(info.builtAt)}
                {stale && (
                  <span className="ml-2 rounded bg-amber-950/50 px-1.5 py-0.5 text-amber-400">
                    media changed since build — rebuild to refresh
                  </span>
                )}
              </>
            ) : (
              "Not built yet — click Rebuild to generate the .acrf file."
            )}
          </div>
        </div>

        <div className="flex shrink-0 gap-2">
          {refingerprintEnabled && (
            <button
              onClick={onRefingerprint}
              disabled={rebuilding || refingerprinting || mediaCount === 0}
              title="Re-extract and re-hash every media file with the current engine, then rebuild the DB. Use after an engine upgrade."
              className="rounded-lg border border-slate-700 px-4 py-2 text-sm text-slate-300 hover:bg-slate-800 disabled:opacity-40"
            >
              {refingerprinting ? `Re-fingerprinting… ${progress ?? ""}` : "Re-fingerprint"}
            </button>
          )}
          <button
            onClick={onRebuild}
            disabled={rebuilding || refingerprinting || mediaCount === 0}
            className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-500 disabled:opacity-40"
          >
            {rebuilding ? "Rebuilding…" : info ? "Rebuild" : "Build offline DB"}
          </button>
          <button
            disabled={!info}
            onClick={() => {
              if (info) downloadWithPicker(offlineDbUrl(project), `${project}-v${info.version}.acrf`);
            }}
            className="rounded-lg border border-slate-700 px-4 py-2 text-sm text-slate-300 hover:bg-slate-800 disabled:pointer-events-none disabled:border-slate-800 disabled:text-slate-600"
          >
            Download
          </button>
        </div>
      </div>

      {(rebuilding || refingerprinting) && (
        <div className="relative mt-4 h-1.5 overflow-hidden rounded-full bg-slate-800">
          <div className="acr-indeterminate-bar" />
        </div>
      )}

      {error && <p className="mt-3 text-sm text-rose-400">{error}</p>}
    </div>
  );
}
